import { useState, useEffect, useRef } from "react";
import { MicOff, Loader2 } from "lucide-react";

interface VoiceDictationProps {
  onResult: (text: string) => void;
  disabled?: boolean;
}

export default function VoiceDictation({ onResult, disabled }: VoiceDictationProps) {
  const [isListening, setIsListening] = useState(false);
  const [activeLang, setActiveLang] = useState<'ar-IQ' | 'en-US' | null>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Initialize SpeechRecognition
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        onResult(transcript);
        setIsListening(false);
        setActiveLang(null);
      };

      recognition.onerror = (event: any) => {
        console.error("Speech recognition error", event.error);
        setIsListening(false);
        setActiveLang(null);
      };

      recognition.onend = () => {
        setIsListening(false);
        setActiveLang(null);
      };

      recognitionRef.current = recognition;
    }
  }, [onResult]);

  const toggleListening = (targetLang: 'ar-IQ' | 'en-US') => {
    if (disabled) return;
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setActiveLang(null);
    } else {
      try {
        if (recognitionRef.current) {
          recognitionRef.current.lang = targetLang;
          recognitionRef.current.start();
          setActiveLang(targetLang);
          setIsListening(true);
        }
      } catch (err) {
        console.error("Microphone start error:", err);
        setIsListening(false);
        setActiveLang(null);
      }
    }
  };

  if (!recognitionRef.current && typeof window !== 'undefined') {
    return (
      <button 
        type="button" 
        disabled
        title="Voice dictation not supported in this browser"
        className="text-slate-300 cursor-not-allowed p-1"
      >
        <MicOff className="w-3.5 h-3.5" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => toggleListening('ar-IQ')}
        disabled={disabled || (isListening && activeLang !== 'ar-IQ')}
        title={isListening && activeLang === 'ar-IQ' ? "إيقاف التسجيل" : "إملاء عربي"}
        className={`w-6 h-6 rounded flex items-center justify-center transition duration-200 ${
          isListening && activeLang === 'ar-IQ'
            ? "bg-rose-100 text-rose-600 animate-pulse border border-rose-200" 
            : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-transparent"
        } ${disabled || (isListening && activeLang !== 'ar-IQ') ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {isListening && activeLang === 'ar-IQ' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-bold leading-none">ع</span>}
      </button>

      <button
        type="button"
        onClick={() => toggleListening('en-US')}
        disabled={disabled || (isListening && activeLang !== 'en-US')}
        title={isListening && activeLang === 'en-US' ? "إيقاف التسجيل" : "English Medical Dictation"}
        className={`w-6 h-6 rounded flex items-center justify-center transition duration-200 ${
          isListening && activeLang === 'en-US'
            ? "bg-rose-100 text-rose-600 animate-pulse border border-rose-200" 
            : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-transparent"
        } ${disabled || (isListening && activeLang !== 'en-US') ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        {isListening && activeLang === 'en-US' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-bold leading-none">EN</span>}
      </button>
    </div>
  );
}
